import type { DragSlice, DragState, ViewerSliceCreator } from "./types";

export const dragInitialState: DragState = {
  draggingInstanceId: null,
  draggingInstanceIds: {},
  dragOffset: [0, 0, 0],
};

export const createDragSlice: ViewerSliceCreator<DragSlice> = (set) => ({
  ...dragInitialState,

  setDragging: (instanceId, offset) =>
    set((state) => {
      // When ending a drag (instanceId === null), bake the current offset
      // into mesh positions so there's no visual snap-back before the server
      // responds with updated transforms.
      const prevIds = state.draggingInstanceIds;
      if (instanceId === null && Object.keys(prevIds).length > 0) {
        const [dx, dy, dz] = state.dragOffset;
        if (dx !== 0 || dy !== 0 || dz !== 0) {
          for (const mesh of Object.values(state.meshes)) {
            const mid = mesh.instance_id || state.meshToInstance[mesh.uuid];
            if (mid && prevIds[mid]) {
              mesh.position = [
                mesh.position[0] + dx,
                mesh.position[1] + dy,
                mesh.position[2] + dz,
              ];
            }
          }
          // The axis overview reads these positions the same way the meshes
          // read theirs, and the drag it was following ends here: without
          // baking, every axis on the robot would snap back to where the
          // module used to be until the server's answer arrived.
          for (const axis of state.jointAxes) {
            if (axis.instance_id && prevIds[axis.instance_id]) {
              axis.position = [
                axis.position[0] + dx,
                axis.position[1] + dy,
                axis.position[2] + dz,
              ];
            }
          }
        }
      }

      if (instanceId !== null) {
        // Dragging any module in a connected tree moves the whole tree: the
        // server applies the drag delta to the tree's root. Walking only
        // downwards left ancestors and siblings behind for the length of the
        // drag, so they appeared to jump the moment the mouse was released.
        // Climb to the root first, then take everything under it.
        const parentOf = new Map<string, string | null>();
        for (const inst of state.instances) {
          parentOf.set(inst.instance_id, inst.parent_instance_id);
        }

        let rootId = instanceId;
        const visited = new Set<string>([rootId]);
        for (;;) {
          const parentId = parentOf.get(rootId);
          // A malformed cycle must not hang the drag.
          if (!parentId || visited.has(parentId)) break;
          rootId = parentId;
          visited.add(rootId);
        }

        const allIds: Record<string, true> = { [rootId]: true };
        // BFS to find all descendants via parent_instance_id
        const queue = [rootId];
        while (queue.length > 0) {
          const parentId = queue.shift()!;
          for (const inst of state.instances) {
            if (inst.parent_instance_id === parentId && !allIds[inst.instance_id]) {
              allIds[inst.instance_id] = true;
              queue.push(inst.instance_id);
            }
          }
        }
        // The dragged instance itself may not be in `instances` yet.
        allIds[instanceId] = true;
        state.draggingInstanceIds = allIds;
      } else {
        state.draggingInstanceIds = {};
      }

      state.draggingInstanceId = instanceId;
      state.dragOffset = offset ?? [0, 0, 0];
    }),

  setDragOffset: (offset) =>
    set((state) => {
      state.dragOffset = offset;
    }),
});
